import { Injectable } from "@angular/core";
import { ApiService } from "../api.service";
import { map, Observable } from "rxjs";
import dayjs from "dayjs";
import { IPlanWeek } from "../../models/plan/plan-week.model";

@Injectable()
export class PlanService {
  private readonly apiPrefix = '/client/plans';

  constructor(
    private api: ApiService,
  ) {
  }

  getWeeks(): Observable<any> {
    return this.api.get(`${this.apiPrefix}/weeks`)
      .pipe(
        map(weeksData => {
          const weeks = weeksData.data as IPlanWeek[];
          const now = dayjs();
          const transformedWeeks = weeks.map(week => {
            const started = week.startedAt ? dayjs(week.startedAt) : null;
            const ended = week.endAt ? dayjs(week.endAt) : null;

            const isActive = started && ended
              ? now.isAfter(started) && now.isBefore(ended) || now.isSame(started, 'day') || now.isSame(ended, 'day')
              : false;

            return {...week, isActive};
          });

          return {
            ...weeksData,
            data: transformedWeeks
          }
        })
      );
  }

  getWeekItems(weekId: number): Observable<any> {
    return this.api.get(`${this.apiPrefix}/weeks/${weekId}/items`);
  }

  assignWeekItem(weekId: number, itemId: number, data: object): Observable<any> {
    return this.api.post(`${this.apiPrefix}/weeks/${weekId}/items/${itemId}/assign`, data);
  }

  unassignWeekItem(weekId: number, itemId: number): Observable<any> {
    return this.api.delete(`${this.apiPrefix}/weeks/${weekId}/items/${itemId}/assign`);
  }

  completeWeekItem(weekId: number, itemId: number): Observable<any> {
    return this.api.post(`${this.apiPrefix}/weeks/${weekId}/items/${itemId}/complete`, {});
  }

  uncompleteWeekItem(weekId: number, itemId: number): Observable<any> {
    return this.api.delete(`${this.apiPrefix}/weeks/${weekId}/items/${itemId}/complete`);
  }

  transferWeekItem(weekId: number, itemId: number, data: object): Observable<any> {
    return this.api.post(`${this.apiPrefix}/weeks/${weekId}/items/${itemId}/transfer`, data);
  }

  initPlan(): Observable<any> {
    return this.api.post(`${this.apiPrefix}/init`, {});
  }

  getActiveWeek(): Observable<any> {
    return this.api.get(`${this.apiPrefix}/active-week`);
  }

  createWeekItem(weekId: number, data: object) {
    return this.api.post(`${this.apiPrefix}/weeks/${weekId}/items`, data);
  }

  deleteWeekItem(itemId: number) {
    return this.api.delete(`${this.apiPrefix}/week-items/${itemId}`);
  }

  copyWeekItem(itemId: number, data: object) {
    return this.api.post(`${this.apiPrefix}/week-items/${itemId}/copy`, data);
  }

  searchWeekItem(filter: { lessonId?: number, lessonTopicId?: number }) {
    return this.api.get(`${this.apiPrefix}/week-items/search?`, Object.entries(filter).map(([k, value]) => `filter[${k}]=${value}`).join('&'));
  }

  changeActiveWeek(targetWeekId: number) {
    return this.api.post(`${this.apiPrefix}/active-week/shift/${targetWeekId}`, {});
  }

  getActiveWeekChangeHistory() {
    return this.api.get(`${this.apiPrefix}/active-week/shift`);
  }

  rollbackChangeActiveWeek() {
    return this.api.delete(`${this.apiPrefix}/active-week/shift`);
  }

  canResetPlans() {
    return this.api.get(`${this.apiPrefix}/can-reset-plan`);
  }

  resetPlan() {
    return this.api.patch(`${this.apiPrefix}/reset-plan`, {});
  }

  getDetailedPlanCategories() {
    return this.api.get(`${this.apiPrefix}/category/detailed`);
  }

  getPlanDetails(planId: number) {
    return this.api.get(`${this.apiPrefix}/${planId}/details`);
  }

  importPlan(planId: number, body: object) {
    return this.api.post(`${this.apiPrefix}/${planId}/import`, body);
  }

  getAppliedPlans() {
    return this.api.get(`${this.apiPrefix}/applied`);
  }

  deleteAppliedPlan(planId: number) {
    return this.api.delete(`${this.apiPrefix}/${planId}/rollback`);
  }
}
